// Results screen — summary of a completed quiz session. POC only:
// no persistence, so refreshing the page wipes this out.

import { RotateCcw, Check, X as XIcon } from 'lucide-react';
import { Link } from '@inertiajs/react';
import Button from '../../ui/Button';
import { cn } from '../../../lib/cn';

export default function QuizResults({
  quiz,
  entityLabels,
  states,            // { [questionId]: state }
  onRestart,
}) {
  const rows = quiz.questions.map((q) => {
    const state = states[q.id];
    const wasCorrect = computeCorrect(q, state);
    const correctLabel = correctLabelFor(q, entityLabels);
    return { q, state, wasCorrect, correctLabel };
  });

  const total = rows.length;
  const score = rows.filter((r) => r.wasCorrect).length;
  const pct = Math.round((score / total) * 100);

  return (
    <div className="max-w-2xl mx-auto py-10 px-6">
      <div className="text-center">
        <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
          {quiz.title}
        </h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          {quiz.course_context}
        </p>
        <div className="mt-6 inline-flex items-baseline gap-3">
          <span className="text-5xl font-bold text-gray-900 dark:text-white tabular-nums">
            {score}
          </span>
          <span className="text-xl text-gray-500 dark:text-gray-400">
            / {total}
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            ({pct}%)
          </span>
        </div>
      </div>

      <ol className="mt-10 space-y-2">
        {rows.map((row, idx) => (
          <li
            key={row.q.id}
            className={cn(
              'flex items-start gap-3 rounded-lg border px-4 py-3',
              'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800',
            )}
          >
            <div
              className={cn(
                'shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center',
                row.wasCorrect
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                  : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400',
              )}
              aria-label={row.wasCorrect ? 'Correct' : 'Incorrect'}
            >
              {row.wasCorrect ? (
                <Check className="w-3.5 h-3.5" />
              ) : (
                <XIcon className="w-3.5 h-3.5" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Question {idx + 1} • {typeLabel(row.q.type)}
              </div>
              <div className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">
                {row.q.prompt}
              </div>
              {row.correctLabel && (
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Answer: <span className="font-medium text-gray-700 dark:text-gray-300">{row.correctLabel}</span>
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>

      <div className="mt-8 flex items-center justify-center gap-3">
        <Button onClick={onRestart} variant="primary">
          <RotateCcw className="w-4 h-4 mr-1.5" />
          Restart
        </Button>
        <Link
          href="/atlas"
          className="text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
        >
          Back to Atlas
        </Link>
      </div>
    </div>
  );
}

// -- helpers ------------------------------------------------------------------

function computeCorrect(q, state) {
  if (!state || !state.submitted) return false;
  if (q.type === 'map_identify') {
    return state.selectedEntityId === q.correct_entity_id;
  }
  if (q.type === 'map_mcq') {
    const opt = q.options.find((o) => o.id === state.selectedOptionId);
    return !!opt?.correct;
  }
  if (q.type === 'map_short_answer') {
    return state.selfAssessment === 'got_it';
  }
  return false;
}

function correctLabelFor(q, entityLabels) {
  if (q.type === 'map_identify') {
    return entityLabels[q.correct_entity_id];
  }
  if (q.type === 'map_mcq') {
    return q.options.find((o) => o.correct)?.text;
  }
  return null;
}

function typeLabel(type) {
  return {
    map_identify:      'Identify on map',
    map_mcq:           'Multiple choice',
    map_short_answer:  'Short answer',
  }[type] || type;
}
